"use client"

import React from "react"
import { UseFormReturn } from "react-hook-form"
import { PlusCircle, Save, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { DebouncedInput } from "@/components/ui/debounced-input"
import { CurrencyInput } from "@/components/ui/currency-input"
import { DatePicker } from "@/components/ui/date-picker"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useLanguage } from "../../contexts/language-context"
import { MilestoneFormArrayData } from "@/lib/schemas"
import { useEngagementLookups } from '@/hooks/use-engagement-lookups'
import { MILESTONE_STATUS } from '@/lib/constants/engagement-codes'
import { useFormatters } from '@/hooks/use-formatters'
import type { MilestoneTransactionInfo } from '@/types/ui/milestone'

interface MilestoneSectionProps {
  form: UseFormReturn<MilestoneFormArrayData>
  onMilestoneAdd: () => void
  onMilestoneRemove: (index: number) => void
  onGenerateMilestoneTransaction?: (milestoneReference: string, amount: number) => void
  canGenerateMilestoneTransaction?: boolean
  milestoneTransactionInfo?: Record<string, MilestoneTransactionInfo>
  generatingMilestoneRef?: string | null
  isReadonly?: boolean
  isHandlingAction?: boolean
  currentAction?: string
  currency?: string
}

const MilestoneSection = ({ 
  form,
  onMilestoneAdd,
  onMilestoneRemove,
  onGenerateMilestoneTransaction,
  canGenerateMilestoneTransaction,
  milestoneTransactionInfo,
  generatingMilestoneRef,
  isReadonly = false,
  isHandlingAction = false,
  currentAction = "",
  currency
}: MilestoneSectionProps) => {
  const { dictionary } = useLanguage()
  const { formatDate } = useFormatters()
  const { getByCategory } = useEngagementLookups()

  const milestones = form.watch('milestones')

  // Helper: Get transaction info for a specific milestone
  const getTransactionInfo = (milestoneReference: string | undefined) => {
    if (!milestoneReference) return undefined
    return milestoneTransactionInfo?.[milestoneReference]
  }

  // Helper: Determine if milestone has existing transactions
  const hasExistingTransactions = (milestoneReference: string | undefined) => {
    const info = getTransactionInfo(milestoneReference)
    return info?.hasTransactions ?? false
  }

  // Helper: Get appropriate button label based on transaction history
  const getInvoiceButtonLabel = (milestoneReference: string | undefined) => {
    const hasTransactions = hasExistingTransactions(milestoneReference)
    return hasTransactions
      ? (dictionary?.engagement?.core?.regenerateInvoice || "Regenerate Invoice")
      : (dictionary?.engagement?.core?.generateInvoice || "Generate Invoice")
  }

  // Helper: Get user initials from full name with dots
  const getUserInitials = (fullName: string): string => {
    if (!fullName.trim()) return ''
    
    return fullName
      .split(' ')
      .filter(part => part.length > 0)
      .map(part => part.charAt(0).toUpperCase())
      .join('.')
      .concat('.')
  }

  // Helper: Format short "last generated" text with user initials
  const formatLastGeneratedShort = (milestoneReference: string | undefined) => {
    const info = getTransactionInfo(milestoneReference)
    if (!info?.mostRecent) return null

    return {
      date: formatDate(info.mostRecent.createdDateTime),
      initials: getUserInitials(info.mostRecent.createdByUserName),
      fullName: info.mostRecent.createdByUserName
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">
          {dictionary?.engagement?.core?.milestones || "Milestones"}
        </Label>
        <Button onClick={onMilestoneAdd} size="sm" className="gap-1" disabled={isReadonly}>
          <PlusCircle className="h-4 w-4" />
          {dictionary?.engagement?.core?.addMilestone || "Add Milestone"}
        </Button>
      </div>

      <Form {...form}>
        {milestones.map((milestone, index) => {
          const generatedInfo = hasExistingTransactions(milestone.reference) 
            ? formatLastGeneratedShort(milestone.reference) 
            : null
          
          return (
            <Card key={milestone.id} className="p-5 card-hover-effect">
              <div className="flex justify-between items-center mb-5">
                <h4 className="font-medium text-base">
                  {dictionary?.engagement?.core?.milestone || "Milestone"} #{index + 1}
                </h4>
                <div className="flex items-center gap-2">
                  {generatedInfo && (
                    <span className="text-sm text-muted-foreground">
                      Last generated: {generatedInfo.date} by{' '}
                      <abbr 
                        title={generatedInfo.fullName} 
                        className="cursor-help"
                      >
                        {generatedInfo.initials}
                      </abbr>
                    </span>
                  )}
                  {canGenerateMilestoneTransaction && 
                  milestone.status === MILESTONE_STATUS.COMPLETED &&
                  milestone.reference && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-success border-success hover:bg-success/10 hover:text-success"
                          onClick={() => onGenerateMilestoneTransaction?.(milestone.reference, milestone.amount || 0)}
                          disabled={isHandlingAction}
                        >
                          <Save className="h-4 w-4 mr-1" />
                          {generatingMilestoneRef === milestone.reference ? 'Generating...' : getInvoiceButtonLabel(milestone.reference)}
                        </Button>
                    )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onMilestoneRemove(index)}
                  disabled={isReadonly}
                  className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">{dictionary?.engagement?.core?.remove || "Remove"}</span>
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField
                control={form.control}
                name={`milestones.${index}.startDate`}
                render={({ field }) => (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-medium">
                      {dictionary?.engagement?.core?.startDate || "Start Date"}
                    </FormLabel>
                    <FormControl>
                      <DatePicker
                        {...field}
                        placeholder="Pick a date"
                        disabled={isReadonly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`milestones.${index}.endDate`}
                render={({ field }) => (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-medium">
                      {dictionary?.engagement?.core?.endDate || "End Date"}
                    </FormLabel>
                    <FormControl>
                      <DatePicker
                        {...field}
                        placeholder="Pick a date"
                        disabled={isReadonly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`milestones.${index}.status`}
                render={({ field }) => (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-medium">
                      {dictionary?.engagement?.core?.status || "Status"}
                    </FormLabel>
                    <FormControl>
                      <Select
                        {...field}
                        onValueChange={field.onChange}
                        disabled={isReadonly}
                      >
                        <SelectTrigger className="form-field-focus">
                          <SelectValue
                            placeholder={dictionary?.engagement?.core?.selectStatus || "Select status"}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {getByCategory('engagement.CodeMilestoneStatus').map(status => (
                            <SelectItem key={status.code} value={status.code!}>
                              {status.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`milestones.${index}.reference`}
                render={({ field }) => (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-medium">
                      {dictionary?.engagement?.core?.milestoneReference || "Milestone Reference"}
                    </FormLabel>
                    <FormControl>
                      <DebouncedInput
                        {...field}
                        readOnly={isReadonly}
                        className="form-field-focus"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`milestones.${index}.description`}
                render={({ field }) => (
                  <FormItem className="space-y-2.5 col-span-1 md:col-span-2">
                    <FormLabel className="text-sm font-medium">
                      {dictionary?.engagement?.core?.milestoneDescription || "Description"}
                    </FormLabel>
                    <FormControl>
                      <DebouncedInput
                        {...field}
                        value={field.value || ''}
                        placeholder={dictionary?.engagement?.core?.milestoneDescriptionPlaceholder || "Enter milestone description"}
                        readOnly={isReadonly}
                        className="form-field-focus"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`milestones.${index}.amount`}
                render={({ field }) => (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-medium">
                      {dictionary?.engagement?.core?.amount || "Amount"}
                    </FormLabel>
                    <FormControl>
                      <CurrencyInput
                        {...field}
                        currency={currency}
                        readOnly={isReadonly}
                        className="form-field-focus"
                        placeholder="Enter amount"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`milestones.${index}.internalBudget`}
                render={({ field }) => (
                  <FormItem className="space-y-2.5">
                    <FormLabel className="text-sm font-medium">
                      {dictionary?.engagement?.core?.internalBudget || "Internal Budget"}
                    </FormLabel>
                    <FormControl>
                      <CurrencyInput
                        {...field}
                        currency={currency}
                        readOnly={isReadonly}
                        className="form-field-focus"
                        placeholder="Enter internal budget"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Card> 
        )})}
      </Form>
    </div>
  )
}

export default MilestoneSection 